import { UserIdSchema } from "@core/domains/identity";
import {
  OrderKeySchema,
  createModel,
  createModelId,
  createModelSchemas,
  generateOrderKey,
  recoverOrderKeyFromId,
} from "@core/foundation/model";
import { singleline } from "@core/foundation/string";
import { z } from "@core/zod";

import { ReservationIdSchema } from "@apis/billing/models";
import {
  MediaAssetIdSchema,
  MediaAssetVisibilitySchema,
} from "@apis/media/models";

import { ID_PREFIX } from "./shared/id-prefix.ts";

const { generateId: generateGenerationId, idSchema: GenerationIdSchema } =
  createModelId({ idPrefix: ID_PREFIX.GENERATION });

export { GenerationIdSchema };

export const GenerationModelSchema = z.enum(["fluxSchnell", "nanoBanana2.1"]);

const GenerationStatusSchema = z.enum([
  "completed",
  "failed",
  "pending",
  "processing",
]);

type GenerationStatus = z.infer<typeof GenerationStatusSchema>;

const FluxSchnellRequestSchema = z.object(
  {
    height: z.int().positive(),
    model: z.literal("fluxSchnell"),
    prompt: z.string(),
    seed: z.int().nonnegative().optional(),
    steps: z.int().positive().optional(),
    width: z.int().positive(),
  },
  {
    height: "The height of the generated images in pixels.",
    model: "The model to generate with, Flux Schnell.",
    prompt: "The text the images are generated from.",
    seed: "The seed the model samples with, for a reproducible result.",
    steps: "The number of inference steps the model runs.",
    width: "The width of the generated images in pixels.",
  },
);

const MAX_NANO_BANANA_IMAGE_REFS = 14;

const NanoBananaAspectRatioSchema = z.enum([
  "1:1",
  "1:4",
  "1:8",
  "16:9",
  "2:3",
  "21:9",
  "3:2",
  "3:4",
  "4:1",
  "4:3",
  "4:5",
  "5:4",
  "8:1",
  "9:16",
  "auto",
]);

const NanoBananaResolutionSchema = z.enum(["1K", "2K", "4K"]);
const NanoBanana21RequestSchema = z.object(
  {
    aspectRatio: NanoBananaAspectRatioSchema,
    imageRefs: z
      .array(MediaAssetIdSchema)
      .refine(
        imageRefs => imageRefs.length <= MAX_NANO_BANANA_IMAGE_REFS,
        singleline`
          A Nano Banana request takes at most ${MAX_NANO_BANANA_IMAGE_REFS}
          image refs
        `,
      )
      .optional(),
    model: z.literal("nanoBanana2.1"),
    prompt: z.string(),
    resolution: NanoBananaResolutionSchema,
  },
  {
    aspectRatio:
      "The aspect ratio of the generated images, or auto for the model's own.",
    imageRefs:
      "The ids of the media assets the model takes as references, at most 14.",
    model: "The model to generate with, Nano Banana 2.1.",
    prompt: "The text the images are generated from.",
    resolution: "The resolution of the generated images: 1K, 2K, or 4K.",
  },
);

export const GenerationRequestSchema = z.discriminatedUnion("model", [
  FluxSchnellRequestSchema,
  NanoBanana21RequestSchema,
]);

export type GenerationRequest = z.infer<typeof GenerationRequestSchema>;

export const Generation = createModel({
  create: (item, schema) => {
    const status: GenerationStatus = "pending";

    return {
      ...schema.decode(item),
      id: generateGenerationId(),
      orderKey: generateOrderKey(),
      status,
    };
  },
  schemas: createModelSchemas({
    base: z.object(
      {
        description: z.string(),
        error: z.string().optional(),
        etag: z.string().optional(),
        id: GenerationIdSchema,
        mediaAssetIds: z.array(MediaAssetIdSchema),
        orderKey: OrderKeySchema,
        principalId: UserIdSchema,
        request: GenerationRequestSchema,
        reservationId: ReservationIdSchema,
        status: GenerationStatusSchema,
        visibility: MediaAssetVisibilitySchema,
      },
      {
        description: "What the image is for, apart from the prompt.",
        error: "The error message the generation failed with.",
        etag: "The generation's version, which storage stamps on every write.",
        id: "The unique identifier for the generation.",
        mediaAssetIds:
          "The ids of the media assets holding the generated images.",
        orderKey:
          "The key ordering the generations by creation, and nothing else.",
        principalId: "The id of the user the generation belongs to.",
        request: "The model and parameters the images are generated with.",
        reservationId: "The id of the credit reservation paying for the run.",
        status:
          "The generation's status: pending, processing, completed, or failed.",
        visibility:
          "The visibility the generated media assets are stored with.",
      },
    ),
    defaults: { mediaAssetIds: [] },
    derive: {
      create: schema =>
        schema.omit({
          error: true,
          etag: true,
          id: true,
          orderKey: true,
          status: true,
        }),
      // oxlint-disable-next-line no-warning-comments
      // TODO(APT-2405): drop once the stored records carry their orderKey.
      recovery: recoverOrderKeyFromId,
      update: schema =>
        schema
          .pick({ error: true, mediaAssetIds: true, status: true })
          .partial(),
    },
  }),
  update: (item, update, schema) => {
    return { ...item, ...schema.decode(update) };
  },
});

export interface Generation extends z.infer<typeof Generation.schema> {}
